//
//  HelpProvider.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module provides help panel state and runs onboarding tours.
//

import { OnboardingTour, type OnboardingTourController } from "@gfazioli/mantine-onboarding-tour";
import { useMantineTheme } from "@mantine/core";
import { useLocalStorage, useMediaQuery, useWindowEvent } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren
} from "react";
import { useMatches } from "react-router-dom";

import { findTourTarget } from "src/components/help/HelpTourTarget";
import HelpTourStepAnchor from "src/components/help/HelpTourStepAnchor";
import HelpContext, { type HelpContextValue } from "src/contexts/help/HelpContext";
import LayoutContext from "src/contexts/layouts/LayoutContext";
import { getHelpRouteKey } from "src/help/helpPages";
import helpTours, { defaultHelpTour, type HelpTourStep } from "src/help/helpTours";
import { applyStoredTourText } from "src/help/tourStepText";
import useCurrentUser from "src/hooks/authentication/useCurrentUser";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import usePageHelp from "src/hooks/data/load/usePageHelp";
import getRoutePattern from "src/utilities/getRoutePattern";
import hasAnyPermission from "src/utilities/hasAnyPermission";

/**
 * Builds the desktop help-panel preference key from a trimmed, lowercase username.
 *
 * Postconditions: Falsy usernames use anonymous, matching the sidebar preference key.
 */
export const getHelpPanelStorageKey = (username?: string) =>
    `tmd-modern-help-open:${(username || "anonymous").trim().toLowerCase()}`;

/**
 * Shares the current route's help identity, panel state and tour controls with the modern layout.
 *
 * Postconditions: The desktop panel state persists per user; the mobile drawer always starts
 * closed. Tour steps take the title and text maintained for them, drop steps switched off there,
 * and are filtered by permission at the layout context when rendered, and by
 * displayed targets when a tour starts. A running tour ends on navigation, on Escape and when its
 * last step is passed.
 * Assumptions: The current step is presented by HelpTourStepAnchor over a proxy of its target, so
 * targets are never wrapped or remounted. The OnboardingTour element below only draws the dimming
 * overlay cut out around that proxy; it is given an inert child because, while started, it re-keys
 * its children, which would otherwise remount the page.
 */
const HelpProvider = ({ children }: PropsWithChildren) => {
    const theme = useMantineTheme();
    const layout = useContext(LayoutContext);
    const permissions = useCurrentUserPermissions();
    const user = useCurrentUser();
    const routeKey = getRoutePattern(useMatches());
    const helpRouteKey = getHelpRouteKey(routeKey);
    const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, true, {
        getInitialValueInEffect: false
    });
    const [desktopOpened, setDesktopOpened] = useLocalStorage<boolean>({
        key: getHelpPanelStorageKey(user?.username),
        defaultValue: false,
        getInitialValueInEffect: false
    });
    const [mobileOpened, setMobileOpened] = useState(false);
    const [activeSteps, setActiveSteps] = useState<HelpTourStep[]>();
    const [stepIndex, setStepIndex] = useState(0);

    const opened = desktop ? desktopOpened : mobileOpened;
    const setOpened = desktop ? setDesktopOpened : setMobileOpened;

    // The page's help is loaded while the panel is open, which is the only place a tour starts.
    const pageHelp = usePageHelp(
        helpRouteKey,
        layout.context.trialId,
        layout.context.institutionId,
        opened
    );
    const storedTourSteps = pageHelp.data?.tourSteps;
    const pageTourSteps = useMemo(
        () => helpTours[routeKey] ?? helpTours[helpRouteKey] ?? [],
        [routeKey, helpRouteKey]
    );

    const tourSteps = useMemo(
        () =>
            applyStoredTourText(
                pageTourSteps.length ? pageTourSteps : defaultHelpTour,
                storedTourSteps
            ).filter(
                step =>
                    !step.permission ||
                    hasAnyPermission(permissions, [step.permission], layout.context)
            ),
        [pageTourSteps, storedTourSteps, permissions, layout.context]
    );

    const endTour = useCallback(() => {
        setActiveSteps(undefined);
        setStepIndex(0);
    }, []);

    /**
     * Returns the permitted steps, of one chapter or all, whose targets are displayed now or hidden
     * in a section the tour can reveal.
     */
    const availableSteps = useCallback(
        (chapter?: string) =>
            tourSteps.filter(
                step => (!chapter || step.chapter === chapter) && findTourTarget(step.id)
            ),
        [tourSteps]
    );

    /** Lists the chapters with available steps, in the order the whole tour reaches them. */
    const getTourChapters = useCallback(() => {
        const counts = new Map<string, number>();
        for (const step of availableSteps()) {
            if (step.chapter) {
                counts.set(step.chapter, (counts.get(step.chapter) ?? 0) + 1);
            }
        }
        return [...counts].map(([chapter, steps]) => ({ chapter, steps }));
    }, [availableSteps]);

    /**
     * Requests a tour over the available steps of one chapter, or of the whole page.
     *
     * Postconditions: With no available step the user is told the tour is unavailable and no tour
     * starts. On mobile the help drawer is closed so the page is visible.
     */
    const startTour = useCallback(
        (chapter?: string) => {
            const steps = availableSteps(chapter);
            if (steps.length === 0) {
                notifications.show({
                    color: "warning",
                    title: "Tour unavailable",
                    message: "None of this page's tour stops are currently shown."
                });
                return;
            }

            if (!desktop) {
                setMobileOpened(false);
            }
            setStepIndex(0);
            setActiveSteps(steps);
        },
        [desktop, availableSteps]
    );

    // A tour describes one page; leaving it abandons the tour rather than stranding the overlay.
    useEffect(endTour, [routeKey, endTour]);

    useWindowEvent("keydown", event => {
        if (activeSteps && event.key === "Escape") {
            endTour();
        }
    });

    const tourController = useMemo<OnboardingTourController | undefined>(() => {
        if (!activeSteps) {
            return undefined;
        }

        const goTo = (index: number) =>
            index >= 0 && index < activeSteps.length ? setStepIndex(index) : endTour();

        return {
            tour: activeSteps,
            currentStep: activeSteps[stepIndex],
            currentStepIndex: stepIndex,
            selectedStepId: activeSteps[stepIndex]?.id,
            setCurrentStepIndex: goTo,
            startTour: () => startTour(),
            endTour,
            skipTour: endTour,
            nextStep: () => goTo(stepIndex + 1),
            prevStep: () => goTo(stepIndex - 1),
            options: { loop: false }
        };
    }, [activeSteps, stepIndex, startTour, endTour]);

    const value = useMemo<HelpContextValue>(
        () => ({
            available: true,
            routeKey,
            helpRouteKey,
            desktop,
            opened,
            open: () => setOpened(true),
            close: () => setOpened(false),
            toggle: () => setOpened(current => !current),
            tourSteps,
            startTour,
            getTourChapters,
            tourController
        }),
        [
            routeKey,
            helpRouteKey,
            desktop,
            opened,
            setOpened,
            tourSteps,
            startTour,
            getTourChapters,
            tourController
        ]
    );

    return (
        <HelpContext.Provider value={value}>
            {children}
            {activeSteps && tourController && (
                <>
                    {/* Keyed by step so the overlay re-measures its cutout for each new target. */}
                    <OnboardingTour
                        key={stepIndex}
                        tour={activeSteps}
                        started
                    >
                        <span hidden />
                    </OnboardingTour>
                    <HelpTourStepAnchor tourController={tourController} />
                </>
            )}
        </HelpContext.Provider>
    );
};

export default HelpProvider;
